import React, { useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { adminService } from '../services/api';

const DEFAULT_SAMPLE =
  'https://www.pokemon.com/static-assets/content-assets/cms2/img/pokedex/full/001.png';

const DEFAULT_START = 1;
const DEFAULT_END = 151;
const RANGE_GEN1_151: [number, number] = [1, 151];
const RANGE_152_251: [number, number] = [152, 251];

function padForZipSegment(n: number, sampleUrl: string): string {
  const m = sampleUrl.match(/(\d+)(\.[a-z0-9]+)$/i);
  const pad = m?.[1]?.length ?? 3;
  return String(n).padStart(pad, '0');
}

const AdminPokemonScraper: React.FC = () => {
  const [sampleUrl, setSampleUrl] = useState(DEFAULT_SAMPLE);
  const [startNumber, setStartNumber] = useState(DEFAULT_START);
  const [endNumber, setEndNumber] = useState(DEFAULT_END);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleExtract = async () => {
    setError(null);
    setSuccess(null);
    const trimmed = sampleUrl.trim();
    if (!trimmed) {
      setError('Paste a sample image URL.');
      return;
    }
    if (!Number.isInteger(startNumber) || !Number.isInteger(endNumber) || startNumber < 1 || endNumber < 1 || startNumber > endNumber) {
      setError('Enter valid start and end numbers (positive integers, start ≤ end).');
      return;
    }
    setLoading(true);
    try {
      const blob = await adminService.downloadPokemonImagesZip({
        sampleUrl: trimmed,
        startNumber,
        endNumber,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const aPad = padForZipSegment(startNumber, trimmed);
      const bPad = padForZipSegment(endNumber, trimmed);
      a.download = `pokemon-images-${aPad}-${bPad}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      const extMatch = trimmed.match(/\.[a-z0-9]+$/i);
      const ext = extMatch?.[0] ?? '.png';
      setSuccess(
        `Download started. Range ${startNumber}–${endNumber} (${aPad}${ext} … ${bPad}${ext} when available). If some URLs fail, open _report.txt inside the archive.`
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to build the archive.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-tab-content">
      <h3>Scraper</h3>
      <p className="text-muted">
        Provide a sample URL whose file name is a zero-padded number (e.g. <code>001.png</code>). The
        server downloads that file pattern for an inclusive number range (default <strong>1–151</strong>;
        use <strong>152–251</strong> for the next batch). Path and extension stay the same; only the
        numeric segment changes.
      </p>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card className="mt-3">
        <Card.Body>
          <Form.Group className="mb-3" controlId="scraperSampleUrl">
            <Form.Label>Sample image URL</Form.Label>
            <Form.Control
              type="url"
              value={sampleUrl}
              onChange={(e) => setSampleUrl(e.target.value)}
              placeholder={DEFAULT_SAMPLE}
              disabled={loading}
            />
            <Form.Text className="text-muted">
              Example: Pokédex full art — the numeric segment is replaced for each index in the range below.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3" controlId="scraperRange">
            <Form.Label>Number range (inclusive)</Form.Label>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Form.Control
                type="number"
                min={1}
                max={2000}
                style={{ maxWidth: '7rem' }}
                value={startNumber}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  setStartNumber(Number.isNaN(n) ? DEFAULT_START : n);
                }}
                disabled={loading}
              />
              <span className="text-muted">to</span>
              <Form.Control
                type="number"
                min={1}
                max={2000}
                style={{ maxWidth: '7rem' }}
                value={endNumber}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  setEndNumber(Number.isNaN(n) ? DEFAULT_END : n);
                }}
                disabled={loading}
              />
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                disabled={loading}
                onClick={() => {
                  setStartNumber(RANGE_GEN1_151[0]);
                  setEndNumber(RANGE_GEN1_151[1]);
                }}
              >
                1–151
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                disabled={loading}
                onClick={() => {
                  setStartNumber(RANGE_152_251[0]);
                  setEndNumber(RANGE_152_251[1]);
                }}
              >
                152–251
              </Button>
            </div>
            <Form.Text className="text-muted">
              At most 500 images per request (server limit).
            </Form.Text>
          </Form.Group>
          <Button variant="primary" onClick={() => void handleExtract()} disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Extracting…
              </>
            ) : (
              <>
                <i className="bi bi-download me-2" aria-hidden />
                Extract
              </>
            )}
          </Button>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminPokemonScraper;
